import { useEffect, useState, useCallback, useMemo } from "react";
import { getAdminEvents } from "../../services/ingroove/adminService";
import { formatCurrency, formatDate } from "../../utils/formatters";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
  Ticket,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const statusConfig = {
  0: { label: "Upcoming", variant: "info" },
  1: { label: "Live", variant: "success" },
  2: { label: "Past", variant: "default" },
};

const ProgressBar = ({ sold, total }) => {
  const pct = total > 0 ? Math.min((sold / total) * 100, 100) : 0;
  return (
    <div className="w-full bg-bg-elevated rounded-full h-1 mt-1">
      <div
        className="h-1 rounded-full bg-primary transition-all"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ totalRecords: 0, totalPages: 0 });

  // Filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [revenueSort, setRevenueSort] = useState("none"); // "asc" | "desc" | "none"
  const [showFilters, setShowFilters] = useState(false);

  const hasActiveFilters = fromDate || toDate || revenueSort !== "none";

  const navigate = useNavigate();

  const clearFilters = () => {
    setFromDate("");
    setToDate("");
    setRevenueSort("none");
  };

  const fetchEvents = useCallback(async (searchVal, pageVal, from, to) => {
    setLoading(true);
    try {
      const res = await getAdminEvents({
        search: searchVal,
        pageNumber: pageVal,
        pageSize: 20,
        fromDate: from || undefined,
        toDate: to || undefined,
      });
      setEvents(res.data);
      setMeta({ totalRecords: res.totalRecords, totalPages: res.totalPages });
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      fetchEvents(search, 1, fromDate, toDate);
    }, 400);
    return () => clearTimeout(timeout);
  }, [search, fromDate, toDate]);

  useEffect(() => {
    fetchEvents(search, page, fromDate, toDate);
  }, [page]);

  // Revenue sort is client-side since data is already loaded
  const sortedEvents = useMemo(() => {
    if (revenueSort === "none") return events;
    return [...events].sort((a, b) => {
      const revenueA = a.ticketRevenue + a.extrasRevenue;
      const revenueB = b.ticketRevenue + b.extrasRevenue;
      return revenueSort === "desc" ? revenueB - revenueA : revenueA - revenueB;
    });
  }, [events, revenueSort]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Events"
        subtitle={`${meta.totalRecords.toLocaleString()} events on the platform`}
      />

      {/* Search + filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
          />
          <input
            type="text"
            placeholder="Search by event or hub name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg text-text-primary placeholder-text-hint focus:outline-none focus:border-primary transition"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters((p) => !p)}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition ${
            showFilters || hasActiveFilters
              ? "border-primary text-primary bg-primary/10"
              : "border-border text-text-muted hover:border-border-hover hover:text-text-secondary"
          }`}
        >
          <SlidersHorizontal size={13} />
          Filters
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-text-muted hover:text-red-400 transition"
          >
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Filter panel */}
      {showFilters && (
        <div className="bg-bg-surface border border-border rounded-xl p-4 flex flex-wrap gap-4">
          {/* Date from */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              From date
            </label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>

          {/* Date to */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              To date
            </label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            />
          </div>

          {/* Revenue sort */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-text-muted uppercase tracking-wide">
              Sort by revenue
            </label>
            <select
              value={revenueSort}
              onChange={(e) => setRevenueSort(e.target.value)}
              className="text-sm bg-bg-base border border-border rounded-lg px-3 py-1.5 text-text-primary focus:outline-none focus:border-primary transition"
            >
              <option value="none">Default</option>
              <option value="desc">Highest first</option>
              <option value="asc">Lowest first</option>
            </select>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-bg-surface border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-bg-elevated text-text-muted text-xs uppercase">
            <tr>
              <th className="px-5 py-3 text-left">Event</th>
              <th className="px-5 py-3 text-left">Hub</th>
              <th className="px-5 py-3 text-left">Date</th>
              <th className="px-5 py-3 text-left">Status</th>
              <th className="px-5 py-3 text-left">Tickets</th>
              <th
                className="px-5 py-3 text-left cursor-pointer hover:text-primary transition select-none"
                onClick={() =>
                  setRevenueSort((s) =>
                    s === "desc" ? "asc" : s === "asc" ? "none" : "desc",
                  )
                }
              >
                Revenue{" "}
                {revenueSort === "desc"
                  ? "↓"
                  : revenueSort === "asc"
                    ? "↑"
                    : ""}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="px-5 py-3">
                      <div className="h-4 bg-bg-elevated rounded w-24" />
                    </td>
                  ))}
                </tr>
              ))
            ) : sortedEvents.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center">
                  <Calendar
                    size={24}
                    className="text-border-hover mx-auto mb-2"
                  />
                  <p className="text-xs text-text-muted">No events found</p>
                </td>
              </tr>
            ) : (
              sortedEvents.map((event) => {
                const status = statusConfig[event.status] ?? statusConfig[0];
                const totalRevenue = event.ticketRevenue + event.extrasRevenue;
                return (
                  <tr
                    key={event.id}
                    className="hover:bg-bg-elevated transition cursor-pointer"
                    onClick={() => navigate(`/ingroove/events/${event.id}`)}
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {event.image ? (
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-bg-elevated flex items-center justify-center flex-shrink-0">
                            <Calendar size={16} className="text-text-muted" />
                          </div>
                        )}
                        <div>
                          <p className="text-text-primary font-medium truncate max-w-[180px]">
                            {event.title}
                          </p>
                          {event.address && (
                            <div className="flex items-center gap-1 text-xs text-text-muted mt-0.5">
                              <MapPin size={10} />
                              <span className="truncate max-w-[160px]">
                                {event.address}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {event.hubImage ? (
                          <img
                            src={event.hubImage}
                            alt={event.hubName}
                            className="w-6 h-6 rounded-md object-cover"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-md bg-bg-elevated" />
                        )}
                        <span className="text-text-secondary text-sm truncate max-w-[120px]">
                          {event.hubName}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-text-muted text-xs">
                      {event.eventDate ? formatDate(event.eventDate) : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <Badge label={status.label} variant={status.variant} />
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1 text-xs text-text-secondary">
                        <Ticket size={11} />
                        <span>
                          {event.ticketsSold} / {event.totalTickets}
                        </span>
                      </div>
                      <ProgressBar
                        sold={event.ticketsSold}
                        total={event.totalTickets}
                      />
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-sm font-medium text-primary">
                        {formatCurrency(totalRevenue)}
                      </p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {formatCurrency(event.ticketRevenue)} tickets
                      </p>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-text-muted">
          <span>
            Page {page} of {meta.totalPages} —{" "}
            {meta.totalRecords.toLocaleString()} events
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
              disabled={page === meta.totalPages}
              className="p-1.5 rounded-lg border border-border hover:border-primary hover:text-primary transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
